// src/lib/tagitSdk.js

import { withConfiguredEntityData } from "@/lib/tagitEventIdentity.mjs";

const PAGE_VIEW_DEDUPE_MS = 2000;
let lastPageView = { path: "", at: 0 };

function getPageViewDedupeState() {
  if (typeof window === "undefined") return lastPageView;
  window.__sproutlyLastPageView ||= lastPageView;
  return window.__sproutlyLastPageView;
}

function setPageViewDedupeState(next) {
  lastPageView = next;
  if (typeof window !== "undefined") {
    window.__sproutlyLastPageView = next;
  }
}

function shouldTrackPageView(path) {
  const now = Date.now();
  const last = getPageViewDedupeState();
  if (last.path === path && now - Number(last.at || 0) < PAGE_VIEW_DEDUPE_MS) {
    return false;
  }
  setPageViewDedupeState({ path, at: now });
  return true;
}

function getConfiguredNativeEntityFields() {
  if (typeof window === "undefined") return {};
  const configured = window.tagItConfig?.data?.[0] || {};
  const entityId = configured.entityId;
  const entityidType = configured.entityidType;
  const entityidAlgo = configured.entityidAlgo;
  const entityidOwner = configured.entityidOwner;

  if (!entityId || !entityidType || !entityidAlgo || !entityidOwner) {
    return {};
  }

  return {
    entityId,
    entityidType,
    entityidAlgo,
    entityidOwner,
  };
}

function withConfiguredEntity(event = {}) {
  const nativeEntity = getConfiguredNativeEntityFields();
  if (!nativeEntity.entityId || event.entityId) return event;
  return {
    ...nativeEntity,
    ...event,
  };
}

function getConfiguredNativeDevice() {
  if (typeof window === "undefined") return {};
  const configured = window.tagItConfig?.data?.[0] || {};
  return configured.nativeDevice || {};
}

function getConfiguredEntityData() {
  if (typeof window === "undefined") return {};
  const configured = window.tagItConfig?.data?.[0] || {};
  return configured.entityData || {};
}

function hasConfiguredNativeDevice() {
  const nativeDevice = getConfiguredNativeDevice();
  return Boolean(nativeDevice.platform || nativeDevice.appId || nativeDevice.bundleId);
}

function getNativeSessionPrefix() {
  const nativeDevice = getConfiguredNativeDevice();
  return nativeDevice.platform === "ios" ? "ios-sess" : "android-sess";
}

function withConfiguredNativeContext(event = {}) {
  if (!hasConfiguredNativeDevice()) return event;
  return {
    ...event,
    sessionId: event.sessionId || getNativeSessionId(),
    device: event.device || buildNativeDeviceData(),
  };
}

function buildConfiguredEventPayload(event = {}) {
  if (typeof window === "undefined") return null;
  const config = window.tagItConfig || {};
  const base = config.data?.[0] || {};
  const nativeEntity = getConfiguredNativeEntityFields();
  if (!config.tagId || !config.sdkURL || !nativeEntity.entityId) return null;

  return {
    tagId: config.tagId,
    eventGuid:
      event.eventGuid ||
      (typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`),
    tagType: "web-sdk-nativeId",
    remember: false,
    acceptCookies: false,
    ...nativeEntity,
    eventType: event.eventType || "view",
    eventSubType: event.eventSubType || "page",
    eventDetails: event.eventDetails || null,
    eventSubContext: event.eventSubContext || base.eventSubContext || config.eventSubContext || null,
    entityData: event.entityData || base.entityData || {},
    privacy: event.privacy || base.privacy || {},
    sessionId: event.sessionId || getNativeSessionId(),
    device: event.device || buildNativeDeviceData(),
    content: event.content || {},
  };
}

function getStorageValue(key) {
  try {
    if (typeof sessionStorage !== "undefined") {
      const sessionValue = sessionStorage.getItem(key);
      if (sessionValue) return sessionValue;
    }
  } catch {}
  try {
    if (typeof localStorage !== "undefined") {
      return localStorage.getItem(key);
    }
  } catch {}
  return null;
}

function setStorageValue(key, value) {
  try {
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(key, value);
  } catch {}
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(key, value);
  } catch {}
}

function getNativeSessionId() {
  if (typeof window === "undefined") return null;
  if (!hasConfiguredNativeDevice()) return null;

  const key = "sproutly:nativeSessionId";
  const prefix = getNativeSessionPrefix();
  const existing = getStorageValue(key);
  if (existing?.startsWith(`${prefix}-`)) return existing;

  const uuid =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const next = `${prefix}-${uuid}`;
  setStorageValue(key, next);
  return next;
}

function storageAvailable(storageName) {
  try {
    const storage = window?.[storageName];
    if (!storage) return false;
    const key = "__sproutly_storage_probe__";
    storage.setItem(key, "1");
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function buildNativeDeviceData() {
  if (typeof window === "undefined") return {};

  const nav = window.navigator || {};
  const screen = window.screen || {};
  const nativeMetadata = getConfiguredNativeDevice();
  const screenResolution =
    screen.width && screen.height ? `${screen.width}x${screen.height}` : "";
  const viewportSize =
    window.innerWidth && window.innerHeight ? `${window.innerWidth}x${window.innerHeight}` : "";
  let timeZone = "";

  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {}

  return {
    browserName: nav.appName || "",
    browserVersion: nav.userAgent || nav.appVersion || "",
    os: nav.platform || "",
    screenResolution,
    viewportSize,
    language: nav.language || "",
    timeZone,
    cookiesEnabled: Boolean(nav.cookieEnabled),
    localStorage: storageAvailable("localStorage"),
    sessionStorage: storageAvailable("sessionStorage"),
    userAgent: nav.userAgent || "",
    ...nativeMetadata,
  };
}

function hasSproutlyTagItConfig() {
  if (typeof window === "undefined") return false;
  const config = window.tagItConfig || {};
  return Boolean(config.tagId && config.sdkURL && config.data?.[0]);
}

function waitForSproutlyTagItConfig(event = {}) {
  if (typeof window === "undefined") return false;
  window.addEventListener(
    "sproutlyTagItConfigReady",
    () => {
      trackTagItEvent(event);
    },
    { once: true }
  );
  return true;
}

async function trackNativeIdentityEvent(event = {}) {
  if (typeof window === "undefined") return false;
  const config = window.tagItConfig || {};
  const payload = buildConfiguredEventPayload(event);
  if (!payload) return false;

  try {
    const res = await fetch(config.sdkURL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      credentials: "include",
    });
    if (!res.ok) {
      console.warn("[tagItSDK] native identity event failed", res.status);
    }
    return true;
  } catch (err) {
    console.warn("[tagItSDK] native identity event failed", err);
    return false;
  }
}

/** Track an event via TagIt SDK (noop if SDK not ready). */
export function trackTagItEvent(event = {}) {
  if (typeof window === "undefined") return;
  if (!hasSproutlyTagItConfig()) {
    waitForSproutlyTagItConfig(event);
    return;
  }

  const nativeEntity = getConfiguredNativeEntityFields();
  if (nativeEntity.entityId) {
    trackNativeIdentityEvent(event);
    return;
  }

  // The browser SDK snapshots its startup config, so include the current
  // authenticated identity with each event after auth state changes.
  const payload = withConfiguredEntity(
    withConfiguredNativeContext(
      withConfiguredEntityData(event, getConfiguredEntityData())
    )
  );
  if (typeof window.tagItSDK === "undefined") {
    // If SDK isn't ready yet, listen once
    document.addEventListener(
      "tagItSDKReady",
      () => {
        try {
          window.tagItSDK.trackEvent(payload);
        } catch (err) {
          console.warn("[tagItSDK] delayed track failed", err);
        }
      },
      { once: true }
    );
    return;
  }
  try {
    window.tagItSDK.trackEvent(payload);
  } catch (err) {
    console.warn("[tagItSDK] track failed", err);
  }
}

/** Convenience: page view */
export function trackPageViewSdk({ path, details, content } = {}) {
  const eventSubContext = path || null;
  if (!shouldTrackPageView(eventSubContext || "")) return;

  trackTagItEvent({
    eventType: "view",
    eventSubType: "page",
    eventDetails: details || null,
    eventSubContext,
    content: content || {},
  });
}
